import { useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StepDefinition } from 'components/PremiumWizard';
import { RootState } from 'redux/root.reducer';
import {
  uploadFileStart,
  resetBel,
  setBelStep,
  setEnquiryDetails,
  setBufferDistance as setBufferDistanceAction,
  setMaxSplitArea as setMaxSplitAreaAction,
  setApplyBuffer as setApplyBufferAction,
  setApplySplit as setApplySplitAction,
  submitBelDetailsStart,
  resetBelPayment,
  validateGeometrySettingsStart
} from 'redux/bulkEnquiryLodgement/actions';
import {
  BelEnquiryDetails,
  BelGeometrySettingsValidation,
  BelPaymentStatus,
  BelValidationSummary
} from 'redux/bulkEnquiryLodgement/types';
import {
  confirmPostPaymentStart,
  clearPaymentState
} from 'redux/payment/actions';
import { selectUser } from 'redux/user/selector';
import { currentUser } from 'redux/session/selectors';
import {
  getBelGeometrySettings,
  isBufferDistanceValid,
  isMaxSplitAreaValid,
  getGeometryValidationErrorBanner,
  BelGeometrySettings,
  BelGeometryValidationErrorBanner
} from '../geometrySettings.utils';
import useBelGeometryValidation from './useBelGeometryValidation';

export type { BelEnquiryDetails };

/** Option ids for `AreaStep` / PremiumWizard when the area step is re-enabled (UI layer). */
export type BelShapeHandling = 'auto-split' | 'keep-original' | null;

const BEL_STEPS: StepDefinition[] = [
  { id: 'price', label: 'Price' },
  { id: 'upload', label: 'Upload' },
  { id: 'details', label: 'Details' },
  { id: 'complete', label: 'Complete' }
];

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

interface UploadedFileDisplay {
  name: string;
  size: string;
  polygonCount?: number;
  isValid: boolean;
}

interface UseBelWizardReturn {
  activeStep: number;
  steps: StepDefinition[];
  uploadedFile: UploadedFileDisplay | null;
  enquiryDetails: BelEnquiryDetails;
  setEnquiryDetails: (details: BelEnquiryDetails) => void;
  goNext: () => void;
  goBack: () => void;
  resetWizard: () => void;
  resetUploadOnly: () => void;
  canProceed: boolean;
  loading: boolean;
  error: string | null;
  processingPhase: string;
  onFileSelected: (file: File) => void;
  totalFeatures: number;
  totalAreaSqm: number;
  bufferDistance: number;
  maxSplitAreaKm: number;
  applyBuffer: boolean;
  applySplit: boolean;
  onBufferDistanceChange: (value: number) => void;
  onMaxSplitAreaChange: (value: number) => void;
  onApplyBufferChange: (apply: boolean) => void;
  onApplySplitChange: (apply: boolean) => void;
  onGeometryInputsValidityChange: (valid: boolean) => void;
  validationSummary: BelValidationSummary | null;
  geometrySettings: BelGeometrySettings | null;
  geometrySettingsValidation: BelGeometrySettingsValidation;
  geometryValidationErrorBanner: BelGeometryValidationErrorBanner | null;
  paymentStatus: BelPaymentStatus;
  checkoutClientSecret: string | null;
  invoiceId: string | null;
  purchaseOrderNumber: string;
  onPurchaseOrderNumberChange: (value: string) => void;
  onConfirmPayment: (purchaseOrderNumber?: string) => void;
}

const selectBel = (state: RootState) => state.belCache;

const useBelWizard = (): UseBelWizardReturn => {
  const dispatch = useDispatch();
  const bel = useSelector(selectBel);
  const user = useSelector(selectUser);
  const sessionUser = useSelector(currentUser);
  const profileUser = user ?? sessionUser;
  const hasProfileAddress = Boolean(profileUser?.Address?.line1);
  const [geometryInputsValid, setGeometryInputsValid] = useState(true);
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState<string>('');
  const currentStepId = BEL_STEPS[bel.currentStep]?.id;

  const geometrySettings = useMemo((): BelGeometrySettings | null => {
    if (!bel.validationSummary) return null;
    return getBelGeometrySettings(
      bel.validationSummary.geometryTypes ?? [],
      bel.validationSummary.hasPolygonExceeding20Km2
    );
  }, [bel.validationSummary]);

  useBelGeometryValidation(geometrySettings);

  const belFileId = bel.fileInfo?.fileId;
  const belTotalFeatures = bel.validationSummary?.totalFeatures;

  useEffect(() => {
    setGeometryInputsValid(true);
  }, [belFileId, belTotalFeatures]);

  const uploadedFile = useMemo((): UploadedFileDisplay | null => {
    if (!bel.fileInfo?.fileId) return null;
    return {
      name: bel.fileInfo.fileName,
      size: bel.fileInfo.fileSize,
      polygonCount: bel.fileInfo.polygonCount || undefined,
      isValid: bel.fileInfo.isValid === true
    };
  }, [bel.fileInfo]);

  const setDetails = useCallback(
    (details: BelEnquiryDetails) => {
      dispatch(setEnquiryDetails(details));
    },
    [dispatch]
  );

  const canProceed = useMemo(() => {
    if (bel.loading) return false;
    switch (currentStepId) {
      case 'price':
        return (
          !bel.livePriceLoading && bel.stripePrice !== null && hasProfileAddress
        );
      case 'upload': {
        const fileValid =
          bel.fileInfo?.isValid === true && bel.processingPhase === 'IDLE';
        if (!fileValid) return false;
        if (bel.validationSummary && bel.validationSummary.totalFeatures === 0)
          return false;
        if (!geometrySettings) return false;

        const bufferActive = geometrySettings.bufferRequired || bel.applyBuffer;
        const splitActive =
          geometrySettings.showSplit &&
          (geometrySettings.splitRequired || bel.applySplit);

        if (
          bufferActive &&
          !isBufferDistanceValid(bel.bufferDistance, geometrySettings)
        )
          return false;
        if (
          splitActive &&
          !isMaxSplitAreaValid(bel.maxSplitAreaKm, geometrySettings)
        )
          return false;

        if (!geometryInputsValid) return false;

        if (bel.geometrySettingsValidation.loading) return false;
        if (bel.geometrySettingsValidation.canProceed !== true) return false;

        return true;
      }
      case 'details': {
        const d = bel.enquiryDetails;
        const hasDates = Boolean(d.digStartAt && d.digEndAt);
        const hasAutoRenewUntil = !d.autoRenew || Boolean(d.autoRenewUntil);
        const hasActivityType =
          d.activityType === 'design' ||
          d.activityType === 'planning' ||
          d.activityType === 'excavation';
        const hasActivity =
          d.activityTypes.length > 0 || Boolean(d.activity?.trim());
        const hasLocations = d.locationTypes.length > 0;
        const fileFields = bel.validationSummary?.fields ?? [];
        const userRefOk =
          !d.useShapeNamesFromFile ||
          !fileFields.length ||
          Boolean(d.userReferenceProperty);
        return (
          hasDates &&
          hasAutoRenewUntil &&
          hasActivityType &&
          hasActivity &&
          hasLocations &&
          userRefOk
        );
      }
      case 'complete':
        return false;
      default:
        return false;
    }
  }, [
    bel,
    currentStepId,
    geometrySettings,
    geometryInputsValid,
    hasProfileAddress
  ]);

  const geometryValidationErrorBanner = useMemo((): BelGeometryValidationErrorBanner | null => {
    const validation = bel.geometrySettingsValidation;
    if (validation.canProceed === false && validation.violations.length > 0) {
      return getGeometryValidationErrorBanner(validation.violations);
    }
    if (validation.error) {
      return { title: 'Error', message: validation.error };
    }
    return null;
  }, [bel.geometrySettingsValidation]);

  const goNext = useCallback(() => {
    if (bel.loading) return;
    switch (currentStepId) {
      case 'price':
        dispatch(setBelStep(1));
        break;
      case 'upload':
        if (canProceed) {
          dispatch(validateGeometrySettingsStart({ pendingStepAdvance: true }));
        }
        break;
      case 'details':
        if (canProceed) dispatch(submitBelDetailsStart());
        break;
      default:
        break;
    }
  }, [bel.loading, canProceed, currentStepId, dispatch]);

  const goBack = useCallback(() => {
    if (bel.currentStep > 0 && currentStepId !== 'complete') {
      dispatch(setBelStep(bel.currentStep - 1));
    } else if (
      currentStepId === 'complete' &&
      bel.paymentStatus === 'invoice'
    ) {
      dispatch(clearPaymentState());
      dispatch(resetBelPayment());
      dispatch(setBelStep(bel.currentStep - 1));
      setPurchaseOrderNumber('');
    }
  }, [bel.currentStep, currentStepId, bel.paymentStatus, dispatch]);

  const resetWizard = useCallback(() => {
    dispatch(resetBel());
    dispatch(setBelStep(0));
    dispatch(clearPaymentState());
  }, [dispatch]);

  const resetUploadOnly = useCallback(() => {
    dispatch(resetBel());
    dispatch(setBelStep(1));
  }, [dispatch]);

  const onFileSelected = useCallback(
    (file: File) => {
      dispatch(uploadFileStart({ file }));
    },
    [dispatch]
  );

  const onBufferDistanceChange = useCallback(
    (value: number) => {
      dispatch(setBufferDistanceAction(value));
    },
    [dispatch]
  );

  const onMaxSplitAreaChange = useCallback(
    (value: number) => {
      dispatch(setMaxSplitAreaAction(value));
    },
    [dispatch]
  );

  const onApplyBufferChange = useCallback(
    (apply: boolean) => {
      dispatch(setApplyBufferAction(apply));
    },
    [dispatch]
  );

  const onApplySplitChange = useCallback(
    (apply: boolean) => {
      dispatch(setApplySplitAction(apply));
    },
    [dispatch]
  );

  const onGeometryInputsValidityChange = useCallback((valid: boolean) => {
    setGeometryInputsValid(valid);
  }, []);

  const onPurchaseOrderNumberChange = useCallback((value: string) => {
    setPurchaseOrderNumber(value);
  }, []);

  const onConfirmPayment = useCallback(() => {
    dispatch(
      confirmPostPaymentStart(Number(bel.invoiceId), purchaseOrderNumber)
    );
  }, [dispatch, bel.invoiceId, purchaseOrderNumber]);

  return {
    activeStep: bel.currentStep,
    steps: BEL_STEPS,
    uploadedFile,
    enquiryDetails: bel.enquiryDetails,
    setEnquiryDetails: setDetails,
    goNext,
    goBack,
    resetWizard,
    resetUploadOnly,
    canProceed,
    loading: bel.loading,
    error: bel.error,
    processingPhase: bel.processingPhase,
    onFileSelected,
    totalFeatures: bel.validationSummary?.totalFeatures ?? 0,
    totalAreaSqm: bel.validationSummary?.totalAreaSqm ?? 0,
    bufferDistance: bel.bufferDistance,
    maxSplitAreaKm: bel.maxSplitAreaKm,
    applyBuffer: bel.applyBuffer,
    applySplit: bel.applySplit,
    onBufferDistanceChange,
    onMaxSplitAreaChange,
    onApplyBufferChange,
    onApplySplitChange,
    onGeometryInputsValidityChange,
    validationSummary: bel.validationSummary,
    geometrySettings,
    geometrySettingsValidation: bel.geometrySettingsValidation,
    geometryValidationErrorBanner,
    paymentStatus: bel.paymentStatus,
    checkoutClientSecret: bel.checkoutClientSecret,
    invoiceId: bel.invoiceId,
    purchaseOrderNumber,
    onPurchaseOrderNumberChange,
    onConfirmPayment
  };
};

export default useBelWizard;
